import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect } from 'react';
import { PASTE_COMMAND, COMMAND_PRIORITY_HIGH } from 'lexical';
import { $createImageNode } from './ImageNode';
import { $generateHtmlFromNodes } from '@lexical/html';
import { MAX_BODY_SIZE_BYTES, getStringByteSize, tryInsertImage } from '../../../../../../utils/imageUploadUtils';

interface ImagePastePluginProps {
  onBodySizeExceeded?: (exceeded: boolean) => void;
}

export function ImagePastePlugin({ onBodySizeExceeded }: ImagePastePluginProps): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerUpdateListener(() => {
      editor.getEditorState().read(() => {
        const html = $generateHtmlFromNodes(editor);
        const currentBodySize = getStringByteSize(html);
        const exceeded = currentBodySize > MAX_BODY_SIZE_BYTES;

        onBodySizeExceeded?.(exceeded);
      });
    });
  }, [editor, onBodySizeExceeded]);

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        const items = event.clipboardData?.items;
        if (!items) return false;

        for (const item of Array.from(items)) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (file) {
              const reader = new FileReader();
              reader.onload = (): void => {
                editor.update(() => {
                  tryInsertImage(editor, reader.result as string, (src: string) => $createImageNode({ src }), {
                    imageType: file.type,
                    onBodySizeExceeded
                  });
                });
              };
              reader.readAsDataURL(file);
              return true;
            }
          }
        }
        return false;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor, onBodySizeExceeded]);

  return null;
}

export default ImagePastePlugin;
